import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { documentiApi, motoApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ImageLightbox } from '../components/ImageLightbox';
import { FileText, Plus, Trash2, Loader2, ExternalLink, AlertTriangle, ZoomIn } from 'lucide-react';
import { toast } from 'sonner';
import { differenceInDays } from 'date-fns';
import { FotoUploader } from '../components/FotoUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIPI_DOCUMENTO = [
  'Libretto',
  'Assicurazione',
  'Patente',
  'Bollo',
  'Revisione',
  'Fattura',
  'Garanzia',
  'Altro'
];

export default function Documenti() {
  const [documenti, setDocumenti] = useState([]);
  const [motoList, setMotoList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [formData, setFormData] = useState({
    moto_id: '',
    tipo: '',
    nome: '',
    file_url: '',
    data_scadenza: '',
    foto_urls: [],
    note: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [docRes, motoRes] = await Promise.all([
        documentiApi.getAll(),
        motoApi.getAll()
      ]);
      setDocumenti(docRes.data);
      setMotoList(motoRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      moto_id: '',
      tipo: '',
      nome: '',
      file_url: '',
      data_scadenza: '',
      foto_urls: [],
      note: ''
    });
  };

  const openLightbox = (images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const getImageUrl = (url) => {
    return url?.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
  };

  const handleOpenDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tipo || !formData.nome || !formData.file_url) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        moto_id: formData.moto_id || null,
        data_scadenza: formData.data_scadenza || null
      };
      await documentiApi.create(dataToSend);
      toast.success('Documento aggiunto');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo documento?')) return;
    
    try {
      await documentiApi.delete(id);
      toast.success('Documento eliminato');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr.includes('.')) {
      const [day, month, year] = dateStr.split('.');
      return new Date(year, month - 1, day);
    }
    return new Date(dateStr);
  };

  const getDaysUntil = (dateStr) => {
    const date = parseDate(dateStr);
    if (!date) return null;
    return differenceInDays(date, new Date());
  };

  const getMotoName = (motoId) => {
    if (!motoId) return null;
    const moto = motoList.find(m => m.id === motoId);
    return moto ? `${moto.marca} ${moto.modello}` : null;
  };

  if (loading) {
    return (
      <Layout>
        <div className="page-container flex items-center justify-center min-h-[60vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container" data-testid="documenti-page">
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Documenti</h1>
            <p className="page-subtitle">Archivio documenti</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={handleOpenDialog}
                className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm h-12 px-6"
                data-testid="add-doc-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Aggiungi
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Teko'] text-2xl text-white uppercase">
                  Nuovo Documento
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label className="form-label">Tipo *</Label>
                  <Select 
                    value={formData.tipo} 
                    onValueChange={(v) => setFormData({...formData, tipo: v})}
                  >
                    <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11" data-testid="doc-tipo-select">
                      <SelectValue placeholder="Tipo documento" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                      {TIPI_DOCUMENTO.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="form-label">Nome *</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    placeholder="es. Libretto Ducati Monster"
                    className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                    data-testid="doc-nome"
                  />
                </div>

                {motoList.length > 0 && (
                  <div>
                    <Label className="form-label">Moto (opzionale)</Label>
                    <Select 
                      value={formData.moto_id} 
                      onValueChange={(v) => setFormData({...formData, moto_id: v === '__none__' ? '' : v})}
                    >
                      <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11" data-testid="doc-moto-select">
                        <SelectValue placeholder="Generale" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                        <SelectItem value="__none__">Generale</SelectItem>
                        {motoList.map(m => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.marca} {m.modello}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="form-label">URL File *</Label>
                  <Input
                    value={formData.file_url}
                    onChange={(e) => setFormData({...formData, file_url: e.target.value})}
                    placeholder="https://drive.google.com/..."
                    className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono text-sm"
                    data-testid="doc-url"
                  />
                  <p className="text-[#9CA3AF] text-xs mt-1">
                    Link a Google Drive, Dropbox o altro cloud storage
                  </p>
                </div>

                <div>
                  <Label className="form-label">Data Scadenza (opzionale)</Label>
                  <Input
                    value={formData.data_scadenza}
                    onChange={(e) => setFormData({...formData, data_scadenza: e.target.value})}
                    placeholder="GG.MM.AAAA"
                    className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                    data-testid="doc-scadenza"
                  />
                </div>

                {/* Foto Upload */}
                <div>
                  <Label className="form-label">Foto del documento</Label>
                  <FotoUploader
                    fotoUrls={formData.foto_urls}
                    setFotoUrls={(urls) => {
                      if (typeof urls === 'function') {
                        setFormData(prev => ({...prev, foto_urls: urls(prev.foto_urls)}));
                      } else {
                        setFormData({...formData, foto_urls: urls});
                      }
                    }}
                    maxPhotos={5}
                  />
                </div>

                <div>
                  <Label className="form-label">Note</Label>
                  <Input
                    value={formData.note}
                    onChange={(e) => setFormData({...formData, note: e.target.value})}
                    placeholder="Note aggiuntive..."
                    className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                    data-testid="doc-note"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="flex-1 h-11 border-[#262626] text-white hover:bg-white/5 uppercase tracking-wider rounded-sm"
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 h-11 bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm"
                    data-testid="doc-submit"
                  >
                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Salva'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {documenti.length === 0 ? (
          <div className="vault-card text-center py-16">
            <FileText className="w-16 h-16 text-[#4B5563] mx-auto mb-4" />
            <h3 className="text-white text-xl mb-2">Nessun documento</h3>
            <p className="text-[#9CA3AF] mb-6">Archivia i tuoi documenti importanti</p>
            <Button 
              onClick={handleOpenDialog}
              className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm h-11 px-6"
            >
              <Plus className="w-5 h-5 mr-2" />
              Aggiungi Documento
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {documenti.map((doc) => {
              const daysUntil = getDaysUntil(doc.data_scadenza);
              const isExpiring = daysUntil !== null && daysUntil <= 30 && daysUntil > 0;
              const isExpired = daysUntil !== null && daysUntil <= 0;
              const motoName = getMotoName(doc.moto_id);

              return (
                <div
                  key={doc.id}
                  className={`vault-card ${isExpired ? 'border-red-500/50' : isExpiring ? 'border-yellow-500/50' : ''}`}
                  data-testid={`doc-item-${doc.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-10 h-10 flex items-center justify-center ${
                      isExpired ? 'bg-red-500/10' : isExpiring ? 'bg-yellow-500/10' : 'bg-[#FF5F1F]/10'
                    }`}>
                      {isExpired || isExpiring ? (
                        <AlertTriangle className={`w-5 h-5 ${isExpired ? 'text-red-500' : 'text-yellow-500'}`} />
                      ) : (
                        <FileText className="w-5 h-5 text-[#FF5F1F]" />
                      )}
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-[#9CA3AF] hover:text-[#FF5F1F] transition-colors"
                        data-testid={`open-doc-${doc.id}`}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="p-2 text-[#9CA3AF] hover:text-red-500 transition-colors"
                        data-testid={`delete-doc-${doc.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <span className="vault-badge vault-badge-orange">{doc.tipo}</span>
                  <h3 className="text-white font-medium text-lg mt-2">{doc.nome}</h3>
                  
                  {motoName && (
                    <p className="text-[#9CA3AF] text-sm mt-1">{motoName}</p>
                  )}

                  {doc.foto_urls && doc.foto_urls.length > 0 && (
                    <div className="grid grid-cols-3 gap-1 mt-3">
                      {doc.foto_urls.slice(0, 3).map((url, idx) => (
                        <div 
                          key={idx} 
                          className="relative cursor-pointer group/img"
                          onClick={() => openLightbox(doc.foto_urls, idx)}
                        >
                          <img 
                            src={getImageUrl(url)} 
                            alt="" 
                            className="w-full h-12 object-cover border border-[#262626]" 
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                            <ZoomIn className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {doc.data_scadenza && (
                    <div className="mt-4 pt-4 border-t border-[#262626]">
                      <p className={`font-mono text-sm ${
                        isExpired ? 'text-red-500' : isExpiring ? 'text-yellow-500' : 'text-[#9CA3AF]'
                      }`}>
                        Scadenza: {doc.data_scadenza}
                        {isExpired && ' (Scaduto)'}
                        {isExpiring && ` (${daysUntil} giorni)`}
                      </p>
                    </div>
                  )}

                  {doc.note && (
                    <p className="text-[#9CA3AF] text-sm mt-3 line-clamp-2">{doc.note}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ImageLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </Layout>
  );
}
